import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FlagIcon from "@mui/icons-material/Flag";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import {
  CssBaseline,
  Container,
  Grid,
  Typography,
  Button,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Switch,
  Popover,
  FormControl,
  Pagination,
  Paper,
  Select,
  InputLabel,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import SalaryIcon from "../Icons/Salary.png";
import TimeSheetIcon from "../Icons/Timesheet.png";
import LeaveIcon from "../Icons/Leave.png";
import projectIcon from "../Icons/project.png";
import EmployeeSalary from "../Components/EmployeeSalary";
import EmployeeProject from "../Components/EmployeeProject";
import LeaveRequest from "../Components/LeaveRequest";
import {
  Add,
  Delete,
  Edit,
  Flag,
  FilterList,
  Settings,
  Search,
  Description,
} from "@mui/icons-material";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const columns = [
  "organization_name", // Changed from bo_id to organization_name for table display
  "employee_id",
  "first_name",
  "last_name",
  "designation",
  "mobile_no",
  "email_id",
  "address",
  "location",
  "insurance",
  "remarks",
];

const columnLabels = {
  organization_name: "Organization Name", // Updated label for organization_name
  employee_id: "Employee ID",
  first_name: "First Name",
  last_name: "Last Name",
  designation: "Designation",
  mobile_no: "Mobile No",
  email_id: "Email Id",
  address: "Address",
  location: "Location",
  insurance: "Insurance",
  remarks: "Remarks",
};

function Employees() {
  const [data, setData] = useState([]);
  const formFields = [
    "employee_id",
    "first_name",
    "last_name",
    "designation",
    "mobile_no",
    "email_id",
    "address",
    "location",
    "data_source",
    "remarks",
  ];
  const [visibleCols, setVisibleCols] = useState({
    organization_name: false, // Changed from bo_id to organization_name
    employee_id: true,
    first_name: true,
    last_name: true,
    designation: true,
    mobile_no: true,
    email_id: true,
    address: true,
    location: true,
    data_source: true,
    ...Object.fromEntries(
      columns
        .filter(
          (c) =>
            ![
              "organization_name",
              "employee_id",
              "first_name",
              "last_name",
              "designation",
              "mobile_no",
              "email_id",
              "address",
              "location",
              "data_source",
              "remarks",
            ].includes(c)
        )
        .map((c) => [c, false])
    ),
  });
  const [filters, setFilters] = useState({});
  const [filterAnchors, setFilterAnchors] = useState({});
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [anchorSettings, setAnchorSettings] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const navigate = useNavigate();
  const [designationOptions, setDesignationOptions] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const query = new URLSearchParams({ page, ...filters }).toString();
      const res = await fetch(
        `${BASE_URL}/api/employees/paginated?${query}`
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Fetch failed");
      setData(json.data || []);
      setTotalPages(Math.ceil(json.total / rowsPerPage));
    } catch (err) {
      console.error("Error fetching employees:", err);
      setAlert({ type: "error", msg: "Failed to load employees" });
    }
  }, [page, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/employees/get-variable-values`
        );
        const data = await res.json();
        setDesignationOptions(data.designations || []);
      } catch (error) {
        console.error("Failed to fetch designation options:", error);
      }
    };

    fetchDesignations();
  }, []);

  const handleFilterOpen = (e, col) =>
    setFilterAnchors({ ...filterAnchors, [col]: e.currentTarget });
  const handleFilterClose = (col) =>
    setFilterAnchors({ ...filterAnchors, [col]: null });
  const handleAdd = () => {
    const now = new Date().toISOString();
    setFormData({
      bo_id: "BFXIND",
      employee_id: "",
      first_name: "",
      last_name: "",
      designation: "",
      mobile_no: "",
      email_id: "",
      address: "",
      location: "",
      data_source: "manual",
      delete_flag: "N",
      created_by: "admin",
      created_on_date: now,
      modified_by: "admin",
      modified_on_date: now,
      remarks: "",
    });
    setIsEditing(false);
    setOpenDialog(true);
  };
  const handleEdit = (emp) => {
    setFormData(emp);
    setIsEditing(true);
    setOpenDialog(true);
  };
  const handleDelete = async (emp) => {
    if (!window.confirm("Confirm delete?")) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/employees/${emp.employee_id}/${emp.bo_id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error("Delete failed");
      await fetchData();
      setAlert({ type: "success", msg: "Employee deleted" });
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", msg: "Failed to delete" });
    }
  };
  const handleToggleFlag = async (emp) => {
    const updatedFlag = emp.delete_flag === "Y" ? "N" : "Y";
    try {
      const payload = {
        delete_flag: updatedFlag,
        modified_by: "ADMIN", // optional if backend adds this
        modified_on_date: new Date(), // optional
        modified_at: new Date(), // optional
      };

      const res = await fetch(
        `${BASE_URL}/api/employees/${emp.employee_id}/${emp.bo_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Toggle failed");

      await fetchData(); // re-fetch updated employee list
      setAlert({ type: "success", msg: "Delete flag updated!" });
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", msg: "Failed to update delete flag" });
    }
  };
  const handleSave = async () => {
    try {
      const method = isEditing ? "PUT" : "POST";
      const now = new Date();
      const updatedPayload = { ...formData };
      if (isEditing) {
        delete updatedPayload.organization_name; // Display-only
        delete updatedPayload.employee_id; // Part of PK
        delete updatedPayload.bo_id;
      }
      const payload = {
        ...updatedPayload,
        delete_flag: "N",
        data_source: "manual",
        created_by: "admin",
        created_on_date: now.toISOString(),
        created_at: now.toISOString(),
        modified_by: "admin",
        modified_on_date: now.toISOString(),
        modified_at: now.toISOString(),
      };
      const url = isEditing
        ? `${BASE_URL}/api/employees/${formData.employee_id}/${formData.bo_id}`
        : `${BASE_URL}/api/employees`;
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save employee");
      await fetchData();
      setAlert({ type: "success", msg: isEditing ? "Updated!" : "Added!" });
      setOpenDialog(false);
    } catch (err) {
      console.error("Error saving employee:", err);
      setAlert({ type: "error", msg: "Save failed" });
    }
  };
  const handleSalary = (emp) => {
    setSelectedEmployeeId(emp.employee_id);
    setSalaryDialogOpen(true);
  };
  const handleLeave = (emp) => {
    setSelectedEmployeeId(emp.employee_id);
    setLeaveDialogOpen(true);
  };
  const handleProject = (emp) => {
    setSelectedEmployeeId(emp.employee_id);
    setProjectDialogOpen(true);
  };
  const handleTimesheet = (emp) => {
    navigate(`/timesheet`);
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h5" gutterBottom fontSize="20px" fontWeight="600">
        Employees
      </Typography>

      {alert.msg && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        gap={1.5}
        mb={2}
      >
        <Button
          onClick={(e) => setAnchorSettings(e.currentTarget)}
          sx={{ minWidth: 0, p: 1 }}
        >
          <SettingsIcon />
        </Button>
        <Menu
          anchorEl={anchorSettings}
          open={Boolean(anchorSettings)}
          onClose={() => setAnchorSettings(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 1,
              width: 200,
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: 2,
            },
          }}
        >
          {columns.map((col) => (
            <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: 1.5,
                  px: 2,
                  py: 1,
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",

                    color: "#333",
                    fontWeight: "bold",
                  }}
                >
                  {columnLabels[col]}
                </Typography>
                <Switch
                  size="small"
                  checked={visibleCols[col]}
                  onChange={(e) =>
                    setVisibleCols((prev) => ({
                      ...prev,
                      [col]: e.target.checked,
                    }))
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#2DB0E4",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2DB0E4",
                    },
                  }}
                />
              </Box>
            </MenuItem>
          ))}
        </Menu>
        <Button
          variant="contained"
          onClick={handleAdd}
          size="small"
          sx={{
            fontWeight: "bold",
            borderRadius: "8px",
            px: 2.5,
            height: 36,
            backgroundColor: "#2DB0E4",
          }}
          startIcon={<Add />}
        >
          Add
        </Button>
        <Box width="250px">
          <TextField
            fullWidth
            label="Search Employees"
            variant="outlined"
            size="small"
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPage(1);
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%", // <-- ensures it fills the available main content space
          overflowX: "auto", // <-- useful for tables to scroll on smaller screens
          p: { xs: 1, sm: 2 }, // optional padding
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    Actions
                  </TableCell>
                  {columns.map(
                    (col) =>
                      visibleCols[col] && (
                        <TableCell key={col}>
                          {columnLabels[col]}
                          <IconButton onClick={(e) => handleFilterOpen(e, col)}>
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                          <Popover
                            open={!!filterAnchors[col]}
                            anchorEl={filterAnchors[col]}
                            onClose={() => handleFilterClose(col)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <DialogContent>
                              <TextField
                                label={`Filter ${columnLabels[col]}`}
                                value={filters[col] || ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    [col]: e.target.value,
                                  })
                                }
                                fullWidth
                              />
                              <DialogActions>
                                <Button
                                  onClick={() => {
                                    setFilters({ ...filters, [col]: "" });
                                    handleFilterClose(col);
                                  }}
                                >
                                  Clear
                                </Button>
                                <Button
                                  onClick={() => handleFilterClose(col)}
                                  variant="contained"
                                >
                                  Filter
                                </Button>
                              </DialogActions>
                            </DialogContent>
                          </Popover>
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((emp) => (
                    <TableRow key={emp.employee_id}>
                      <TableCell>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(emp)}
                            sx={{ color: "#2DB0E4" }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(emp)}
                          >
                            <DeleteIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleFlag(emp)}
                            title="Toggle Delete Flag"
                          >
                            <FlagIcon
                              style={{
                                color:
                                  emp.delete_flag === "N"
                                    ? "#2E9F50"
                                    : "#FF0C0C",
                                transition: "color 0.2s",
                                width: 17,
                                height: 17,
                                verticalAlign: "middle",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleSalary(emp)}
                            title="View Salary"
                          >
                            <img
                              src={SalaryIcon}
                              alt="Salary"
                              style={{ width: 17, height: 17 }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleLeave(emp)}
                            title="Manage Leave"
                          >
                            <img
                              src={LeaveIcon}
                              alt="Leave"
                              style={{ width: 17, height: 17 }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleProject(emp)}
                            title="View Projects"
                          >
                            <img
                              src={projectIcon}
                              alt="Project"
                              style={{ width: 17, height: 17 }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleTimesheet(emp)}
                            title="View TimeSheet"
                          >
                            <img
                              src={TimeSheetIcon}
                              alt="TimeSheet"
                              style={{ width: 17, height: 17 }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                      {columns.map(
                        (col) =>
                          visibleCols[col] && (
                            <TableCell key={col}>{emp[col] || "-"}</TableCell>
                          )
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        columns.filter((col) => visibleCols[col]).length + 1
                      }
                      align="center"
                    >
                      No matching data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "8px",
                  minWidth: "32px",
                  height: "32px",
                  fontSize: "14px",
                },
                "& .Mui-selected": {
                  backgroundColor: "#b3dbea !important",
                  color: "black",
                  fontWeight: "bold",
                },
                "& .MuiPaginationItem-ellipsis": {
                  display: "none",
                },
              }}
            />
          </Box>
        </ThemeProvider>

        <ThemeProvider theme={FormThemes}>
          <CssBaseline />
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            fullWidth
            maxWidth="sm"
            sx={{
              "& .MuiDialog-paper": { borderRadius: 5, overflow: "visible" },
            }}
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {isEditing ? "Edit Employee" : "Add Employee"}
              <IconButton
                aria-label="close"
                onClick={() => setOpenDialog(false)}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                p: "18px 24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="First Name"
                    name="first_name"
                    value={formData.first_name || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    fullWidth
                    sx={{ width: "200px" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    fullWidth
                    sx={{ width: "200px" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel id="designation-label">Designation</InputLabel>
                    <Select
                      labelId="designation-label"
                      value={formData.designation || ""}
                      label="Designation"
                      sx={{ width: "200px", height: "37px" }}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          designation: e.target.value,
                        })
                      }
                    >
                      {designationOptions.map((designation, index) => (
                        <MenuItem key={index} value={designation}>
                          {designation}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="Mobile No"
                    name="mobile_no"
                    value={formData.mobile_no || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, mobile_no: e.target.value })
                    }
                    fullWidth
                    sx={{ width: "200px" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="Email Id"
                    name="email_id"
                    value={formData.email_id || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, email_id: e.target.value })
                    }
                    fullWidth
                    sx={{ width: "200px" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="Location"
                    name="location"
                    value={formData.location || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    fullWidth
                    sx={{ width: "200px" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="Address"
                    name="address"
                    multiline
                    minRows={4}
                    value={formData.address || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    fullWidth
                    sx={{ width: "200px" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Remarks"
                    name="remarks"
                    multiline
                    minRows={4}
                    value={formData.remarks || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, remarks: e.target.value })
                    }
                    fullWidth
                    sx={{ width: "200px" }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setOpenDialog(false)}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                sx={{
                  ...(isEditing && {
                    backgroundColor: "#4CAF50",
                    "&:hover": { backgroundColor: "#45a049" },
                  }),
                }}
              >
                {isEditing ? "Update" : "Add"} Employee
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={salaryDialogOpen}
            onClose={() => setSalaryDialogOpen(false)}
            fullWidth
            maxWidth="lg"
            sx={{
              "& .MuiDialog-paper": { borderRadius: 5, overflow: "visible" },
            }}
          >
            <EmployeeSalary
              open={salaryDialogOpen}
              onClose={() => setSalaryDialogOpen(false)}
              employeeId={selectedEmployeeId}
            />
          </Dialog>
          <Dialog
            open={projectDialogOpen}
            onClose={() => setProjectDialogOpen(false)}
            fullWidth
            maxWidth="lg"
            sx={{
              "& .MuiDialog-paper": { borderRadius: 5, overflow: "visible" },
            }}
          >
            <EmployeeProject
              open={projectDialogOpen}
              onClose={() => setProjectDialogOpen(false)}
              employeeId={selectedEmployeeId}
            />
          </Dialog>
          <Dialog
            open={leaveDialogOpen}
            onClose={() => setLeaveDialogOpen(false)}
            fullWidth
            maxWidth="lg"
            sx={{
              "& .MuiDialog-paper": { borderRadius: 5, overflow: "visible" },
            }}
          >
            <LeaveRequest
              open={leaveDialogOpen}
              onClose={() => setLeaveDialogOpen(false)}
              employeeId={selectedEmployeeId}
            />
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
}

export default Employees;
